import { Component } from '@theme/component';
import { CartAddEvent, QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, fetchConfig, resetShimmer, checkCartLineLimit, showCartLimitWarning } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages the quick order list section.
 *
 * @typedef {object} QuickOrderListComponentRefs
 * @property {HTMLTableRowElement[]} variantRows - The variant row elements
 * @property {HTMLElement} confirmationPanel - The remove all confirmation dialog
 * @property {HTMLElement} totalInfo - The total info section element
 * @property {HTMLElement} errorContainer - The error message container
 * @property {HTMLElement} errorText - The error message text element
 * @property {HTMLElement} successContainer - The success message container
 * @property {HTMLElement} successText - The success message text element
 * @property {HTMLElement} [paginationNav] - The pagination navigation element
 *
 * @extends Component<QuickOrderListComponentRefs>
 */
class QuickOrderListComponent extends Component {
  requiredRefs = [
    'variantRows',
    'confirmationPanel',
    'totalInfo',
    'errorContainer',
    'errorText',
    'successContainer',
    'successText',
  ];

  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {(event: Event) => void} */
  #debouncedHandleQuantityUpdate;

  /** @type {(event: Event) => void} */
  #boundHandleCartUpdate;

  /** @type {(event: Event) => void} */
  #debouncedSetAllQuantityUpdate;

  /**
   * Gets the current page number from pagination controls
   * @returns {number}
   */
  get currentPage() {
    if (this.refs.paginationNav && this.refs.paginationNav.dataset.current_page) {
      const pageNum = parseInt(this.refs.paginationNav.dataset.current_page, 10);
      if (!isNaN(pageNum)) {
        return pageNum;
      }
    }
    return 1;
  }

  /**
   * Gets all cart variant IDs for the product from the data attribute
   * @returns {number[]}
   */
  get cartVariantIds() {
    const data = this.dataset.cartVariantIds;
    if (!data) return [];

    return JSON.parse(data);
  }

  connectedCallback() {
    super.connectedCallback();

    this.#debouncedHandleQuantityUpdate = debounce(this.#handleQuantityUpdate.bind(this), 300);
    this.#boundHandleCartUpdate = this.#handleCartUpdate.bind(this);
    this.#debouncedSetAllQuantityUpdate = debounce(this.#handleSetAllQuantityChange.bind(this), 300);

    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedHandleQuantityUpdate);
    document.addEventListener(ThemeEvents.cartUpdate, this.#boundHandleCartUpdate);
    this.addEventListener('keydown', this.#handleKeyDown, true);
    this.addEventListener('keyup', this.#handleKeyup, true);

    this.#getSetAllInputs().forEach((input) => {
      input.addEventListener('input', this.#handleSetAllInputChange.bind(this));
    });

    this.#initSetAllQuantityFromVariants();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedHandleQuantityUpdate);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#boundHandleCartUpdate);
    this.removeEventListener('keydown', this.#handleKeyDown, true);
    this.removeEventListener('keyup', this.#handleKeyup, true);

    this.#abortController?.abort();
  }

  /**
   * @param {EventTarget | null} target
   * @returns {target is HTMLInputElement}
   */
  #isQuantityInput(target) {
    return target instanceof HTMLInputElement && target.matches('input[type="number"][data-cart-quantity]');
  }

  /**
   * Keyboard navigation:
   * Enter key selects next quantity input
   * Shift+Enter selects previous quantity input
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Enter' || !this.#isQuantityInput(event.target)) {
      return;
    }
    event.preventDefault();

    // Get all VISIBLE quantity inputs (exclude hidden mobile/desktop variants)
    const allQuantityInputs = Array.from(this.querySelectorAll('input[type="number"][data-cart-quantity]')).filter(
      (input) => {
        return input instanceof HTMLElement && input.offsetParent !== null;
      }
    );

    if (allQuantityInputs.length <= 1) {
      return;
    }

    const currentIndex = allQuantityInputs.indexOf(event.target);
    if (currentIndex === -1) {
      return;
    }

    const offset = event.shiftKey ? -1 : 1;
    const nextIndex = (currentIndex + offset + allQuantityInputs.length) % allQuantityInputs.length;
    const nextInput = allQuantityInputs[nextIndex];

    event.target.blur();
    if (nextInput instanceof HTMLInputElement) {
      nextInput.select();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyup = (event) => {
    if ((event.key === 'Tab' || event.key === 'Enter') && this.#isQuantityInput(event.target)) {
      this.#scrollToCenter(event.target);
    }
  };

  /**
   * @param {HTMLElement} element
   */
  #scrollToCenter(element) {
    element.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    });
  }

  /**
   * Handles pagination events
   * @param {Object<string, string>} data - URL search params
   * @param {Event} event - The click event
   */
  async onPaginationControlClick(data, event) {
    event.preventDefault();
    const sectionId = this.dataset.sectionId;

    if (!this.dataset.url || !sectionId) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const newURL = new URL(this.dataset.url, window.location.origin);
    for (const [key, value] of Object.entries(data)) {
      newURL.searchParams.set(key, value);
    }

    await sectionRenderer.renderSection(sectionId, {
      url: newURL,
    });
    this.#scrollToTopOfSection();
  }

  /**
   * Handles removing a single variant item (sets quantity to 0)
   * @param {string} variantId - The variant ID to remove
   * @param {Event} event - The click event
   */
  async onLineItemRemove(variantId, event) {
    event.preventDefault();

    const targetRow = this.refs.variantRows.find((row) => row.dataset.variantId === String(variantId));
    if (!(targetRow instanceof HTMLElement)) return;

    const quantityInput = targetRow.querySelector('input[type="number"]');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = '0';
      quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(0, Number(quantityInput.dataset.cartLine)));
    }
  }

  /**
   * Handles removing all items from the cart
   * @param {Event} event - The click event
   */
  async onRemoveAll(event) {
    event.preventDefault();
    const idsToRemove = this.cartVariantIds;

    this.#clearSuccessMessage();
    this.#clearErrorMessage();
    this.#applyShimmerEffects(idsToRemove);

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      /** @type {Record<string, number>} */
      const updates = {};

      if (idsToRemove.length > 0) {
        for (const variantId of idsToRemove) {
          updates[String(variantId)] = 0;
        }
      }

      if (Object.keys(updates).length === 0) {
        resetShimmer(this);
        return;
      }

      const sectionIds = this.#getSectionIds();
      const sectionsUrl = new URL(window.location.pathname, window.location.origin);
      sectionsUrl.searchParams.set('page', this.currentPage.toString());

      const body = JSON.stringify({
        updates: updates,
        sections: sectionIds.join(','),
        sections_url: sectionsUrl.pathname + sectionsUrl.search,
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body }),
        signal: this.#abortController.signal,
      });

      const responseText = await response.text();
      const data = JSON.parse(responseText);

      resetShimmer(this);

      if (data.errors) {
        this.#showErrorMessage(data.errors);
      } else {
        this.#updateSectionHTML(data);
        this.#toggleConfirmationPanel(false);
        this.#syncSetAllInputs(0);

        document.dispatchEvent(
          new CartAddEvent(data, this.id, {
            source: 'quick-order-remove-all',
            sections: data.sections,
          })
        );
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        resetShimmer(this);
        throw error;
      }
    }
  }

  /**
   * Handles quantity selector updates
   * @param {CustomEvent} event - The quantity update event
   */
  async #handleQuantityUpdate(event) {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;

    // Only handle events from our own quantity selectors
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    let { quantity } = event.detail;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const variantRow = this.refs.variantRows.find((row) => {
      return row.contains(target);
    });

    if (!variantRow) return;

    const variantId = variantRow.dataset.variantId;
    if (!variantId) return;

    const quantityInput = /** @type {HTMLInputElement|null} */ (variantRow.querySelector('input[data-cart-quantity]'));
    const currentCartQuantity = quantityInput ? parseInt(quantityInput.dataset.cartQuantity || '0') || 0 : 0;

    this.#clearSuccessMessage();
    this.#clearErrorMessage();
    this.#clearVariantError(variantId);

    const rules = this.#getVariantQuantityRules(Number(variantId));
    const { isValid, adjustedQuantity } = this.#validateStep(quantity, rules.min, rules.step);

    if (!isValid) {
      quantity = adjustedQuantity;

      if (quantityInput) {
        quantityInput.value = adjustedQuantity.toString();
      }

      const errorMessage = this.#getStepErrorMessage(rules.step, adjustedQuantity);

      this.#showVariantError(variantId, errorMessage);

      setTimeout(() => {
        this.#clearVariantError(variantId);
      }, 3000);
    }

    if (currentCartQuantity === quantity) {
      return;
    }

    // Check cart line limit if adding a new line item (quantity was 0, now > 0)
    if (currentCartQuantity === 0 && quantity > 0) {
      const limitCheck = await checkCartLineLimit({ id: variantId });
      if (!limitCheck.allowed) {
        showCartLimitWarning(limitCheck.message);
        const allQuantityInputs = variantRow.querySelectorAll('input[data-cart-quantity]');
        if (allQuantityInputs.length > 0) {
          allQuantityInputs.forEach((input) => {
            if (input instanceof HTMLInputElement) {
              input.value = '0';
            }
          });
        }
        return;
      }
    }

    this.#applyShimmerEffects([variantId]);

    this.#disableQuickOrderListItems();
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      /** @type {Record<string, number>} */
      const updates = {};
      updates[variantId] = quantity;

      // Include page parameter in sections URL to maintain pagination state
      const sectionsUrl = new URL(window.location.pathname, window.location.origin);
      sectionsUrl.searchParams.set('page', this.currentPage.toString());

      const body = JSON.stringify({
        updates: updates,
        sections: this.#getSectionIds().join(','),
        sections_url: sectionsUrl.pathname + sectionsUrl.search,
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body }),
        signal: this.#abortController.signal,
      });

      const responseText = await response.text();
      const data = JSON.parse(responseText);

      resetShimmer(this);

      if (data.errors) {
        this.#showErrorMessage(data.errors);
        if (this.dataset.sectionId) {
          const url = new URL(window.location.href);
          url.searchParams.set('page', this.currentPage.toString());
          await sectionRenderer.renderSection(this.dataset.sectionId, { cache: false, url });
        }
      } else {
        this.#updateSectionHTML(data);
        this.#updateSetAllInputAfterVariantChange();

        const quantityAdded = quantity - currentCartQuantity;
        if (quantityAdded > 0) {
          this.#showSuccessMessage(quantityAdded);
        }

        document.dispatchEvent(
          new CartAddEvent(data, this.id, {
            source: 'quick-order-quantity',
            variantId: variantId,
            sections: data.sections,
          })
        );
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.#enableQuickOrderListItems();
        resetShimmer(this);
        throw error;
      }
    }
  }

  /**
   * Handles cart update events from other components
   * @param {CustomEvent} event - The cart update event
   */
  async #handleCartUpdate(event) {
    // Don't process our own events to avoid double updates
    // Check if this event came from our own quantity update
    if (event.detail?.source === 'quick-order-quantity' && event.detail?.sourceId === this.id) {
      return;
    }

    this.#enableQuickOrderListItems();
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    if (event.detail?.data?.sections && this.dataset.sectionId) {
      this.#updateSectionHTML(event.detail.data);
      if (event.detail.data.sections[this.dataset.sectionId]) {
        return;
      }
    }

    if (this.dataset.sectionId) {
      const url = new URL(window.location.href);
      url.searchParams.set('page', this.currentPage.toString());

      await sectionRenderer.renderSection(this.dataset.sectionId, {
        cache: false,
        url,
      });
    }
  }

  #disableQuickOrderListItems() {
    this.classList.add('quick-order-list-disabled');
  }

  #enableQuickOrderListItems() {
    this.classList.remove('quick-order-list-disabled');
  }

  /**
   * Shows the remove all confirmation dialog
   * @param {Event} event - The click event
   */
  showRemoveAllConfirmation(event) {
    event.preventDefault();
    this.#toggleConfirmationPanel(true);
  }

  /**
   * Hides the remove all confirmation
   * @param {Event} event - The click event
   */
  hideRemoveAllConfirmation(event) {
    event.preventDefault();
    this.#toggleConfirmationPanel(false);
  }

  /**
   * Toggles the confirmation panel visibility
   * @param {boolean} show
   */
  #toggleConfirmationPanel(show) {
    this.refs.confirmationPanel.classList.toggle('hidden', !show);
    this.refs.totalInfo.classList.toggle('confirmation-visible', show);
  }

  /**
   * Shows an error message in the error container
   * @param {string} message - The error message to display
   */
  #showErrorMessage(message) {
    this.refs.errorText.textContent = message;
    this.refs.errorContainer.classList.remove('hidden');
  }

  /**
   * Hides the error messages
   */
  #clearErrorMessage() {
    this.refs.errorContainer.classList.add('hidden');
  }

  /**
   * Shows success message in the success container
   * @param {number} quantityAdded - The number of items added
   */
  #showSuccessMessage(quantityAdded) {
    this.#clearErrorMessage();

    const oneItemText = Theme?.translations?.items_added_to_cart_one || '1 item added to cart';
    const itemsText = Theme?.translations?.items_added_to_cart_other || '{{ count }} items added to cart';

    const message = quantityAdded === 1 ? oneItemText : itemsText.replace('{{ count }}', quantityAdded.toString());

    this.refs.successText.textContent = message;
    this.refs.successContainer.classList.remove('hidden');
  }

  #clearSuccessMessage() {
    this.refs.successContainer.classList.add('hidden');
  }

  /**
   * @param {string} variantId - The variant ID
   * @param {string} message - The error message
   */
  #showVariantError(variantId, message) {
    const variantRow = this.refs.variantRows.find((row) => row.dataset.variantId === String(variantId));
    if (!variantRow) return;

    const errorElements = variantRow.querySelectorAll('[data-variant-error]');
    errorElements.forEach((errorElement) => {
      const textElement = errorElement.querySelector('.variant-item__error-text');
      if (textElement) {
        textElement.textContent = message;
      }
      errorElement.classList.remove('hidden');
    });
  }

  /**
   * @param {string} variantId - The variant ID
   */
  #clearVariantError(variantId) {
    const variantRow = this.refs.variantRows.find((row) => row.dataset.variantId === String(variantId));
    if (!variantRow) return;

    const errorElements = variantRow.querySelectorAll('[data-variant-error]');
    errorElements.forEach((errorElement) => {
      errorElement.classList.add('hidden');
    });
  }

  #clearAllVariantErrors() {
    const errorElements = this.querySelectorAll('[data-variant-error]');
    errorElements.forEach((errorElement) => {
      errorElement.classList.add('hidden');
    });
  }

  /**
   * @param {string} message - The error message
   */
  #showSetAllError(message) {
    const errorElements = this.querySelectorAll('[data-set-all-error]');
    errorElements.forEach((errorElement) => {
      const textElement = errorElement.querySelector('.variant-item__error-text');
      if (textElement) {
        textElement.textContent = message;
      }
      errorElement.classList.remove('hidden');
    });
  }

  #clearSetAllError() {
    const errorElements = this.querySelectorAll('[data-set-all-error]');
    errorElements.forEach((errorElement) => {
      errorElement.classList.add('hidden');
    });
  }

  /**
   * @param {number} quantity - The desired quantity
   * @param {number} min - Minimum quantity
   * @param {number} step - Step increment
   * @returns {{ isValid: boolean, adjustedQuantity: number }}
   */
  #validateStep(quantity, min, step) {
    if (quantity === 0) {
      return { isValid: true, adjustedQuantity: 0 };
    }

    const remainder = (quantity - min) % step;
    if (remainder === 0) {
      return { isValid: true, adjustedQuantity: quantity };
    }

    const adjustedQuantity = min + Math.round((quantity - min) / step) * step;
    return { isValid: false, adjustedQuantity: Math.max(min, adjustedQuantity) };
  }

  /**
   * @param {number} step - Step increment
   * @param {number} adjusted - Adjusted quantity
   * @returns {string}
   */
  #getStepErrorMessage(step, adjusted) {
    return (Theme?.translations?.quantity_step_error || 'Quantity must be in increments of {{ step }}. Adjusted to {{ adjusted }}.')
      .replace('{{ step }}', step.toString())
      .replace('{{ adjusted }}', adjusted.toString());
  }

  /**
   * Applies shimmer effects to price elements
   * @param {Array<string|number>} variantIds - Array of variant IDs to apply shimmer to
   */
  #applyShimmerEffects(variantIds) {
    for (const variantId of variantIds) {
      const variantRow = this.refs.variantRows.find((row) => row.dataset.variantId === String(variantId));
      if (variantRow) {
        const variantTotal = /** @type {import('./utilities').TextComponent|null} */ (
          variantRow.querySelector('.variant-item__total-price')
        );
        variantTotal?.shimmer();
      }
    }

    const totalPrice = /** @type {import('./utilities').TextComponent|null} */ (
      this.querySelector('text-component[ref="totalPrice"]')
    );
    totalPrice?.shimmer();
  }

  #scrollToTopOfSection() {
    // Defer layout read until scroll action to batch with other layout work
    requestAnimationFrame(() => {
      const top = this.getBoundingClientRect().top;
      window.scrollTo({ top: top + window.scrollY, behavior: 'smooth' });
    });
  }

  /**
   * Updates section HTML using morphSection
   * @param {{ sections?: Record<string, string> }} data - Response data containing sections
   */
  #updateSectionHTML(data) {
    if (data.sections && this.dataset.sectionId) {
      const sectionHtml = data.sections[this.dataset.sectionId];
      if (sectionHtml) {
        morphSection(this.dataset.sectionId, sectionHtml);
      }
    }
  }

  /**
   * Gets the section IDs for updating
   * @returns {string[]} Array of section IDs
   */
  #getSectionIds() {
    const sectionIds = [];

    if (this.dataset.sectionId) {
      sectionIds.push(this.dataset.sectionId);
    }

    // Also include all cart-items-component sections (like cart drawer) for smooth updates
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    for (const component of cartItemsComponents) {
      if (!(component instanceof HTMLElement)) continue;
      if (component.dataset.sectionId && !sectionIds.includes(component.dataset.sectionId)) {
        sectionIds.push(component.dataset.sectionId);
      }
    }

    return sectionIds;
  }

  // ============================================
  // SET ALL QUANTITY METHODS
  // ============================================

  /**
   * @param {number} variantId - The variant ID
   * @returns {{ min: number, step: number, max: number|null }}
   */
  #getVariantQuantityRules(variantId) {
    const row = this.querySelector(`[data-variant-id="${variantId}"]`);
    const input = row?.querySelector('input[type="number"][data-cart-quantity]');
    if (!input) {
      return { min: 1, step: 1, max: null };
    }
    const min = parseInt(input.getAttribute('data-min') || '1', 10) || 1;
    const step = parseInt(input.getAttribute('step') || '1', 10) || 1;
    const max = input.getAttribute('max') ? parseInt(input.getAttribute('max') || '0', 10) : null;
    return { min, step, max };
  }

  /**
   * @param {number} value - The desired quantity (e.g. from Set All input)
   * @param {{ min: number, step: number, max: number|null }} rules - Variant rules
   * @returns {number}
   */
  #snapQuantityToRules(value, rules) {
    if (value === 0) return 0;
    const { min, max } = rules;
    let { step } = rules;
    if (step <= 0) step = 1;
    const effectiveMax = max != null ? Math.min(max, value) : value;
    const clamped = Math.max(min, effectiveMax);
    const snapped = min + Math.round((clamped - min) / step) * step;
    if (max != null && snapped > max) return max;
    return snapped;
  }

  /**
   * Initializes the set-all input value based on variant quantities.
   */
  #initSetAllQuantityFromVariants() {
    const commonQuantity = this.#getCommonVariantQuantity();
    this.#syncSetAllInputs(commonQuantity);
  }

  #updateSetAllInputAfterVariantChange() {
    const commonQuantity = this.#getCommonVariantQuantity();
    const currentSetAllValue = parseInt(this.#getVisibleSetAllInput()?.value || '0', 10);

    if (commonQuantity !== currentSetAllValue) {
      this.#syncSetAllInputs(commonQuantity);
    }
  }

  /**
   * @returns {number}
   */
  #getCommonVariantQuantity() {
    const quantityInputs = this.querySelectorAll('input[type="number"][data-cart-quantity]');
    if (quantityInputs.length === 0) {
      return 0;
    }

    const quantities = Array.from(quantityInputs).map((input) => {
      const cartQty = input.getAttribute('data-cart-quantity');
      return cartQty ? parseInt(cartQty, 10) : 0;
    });

    const firstQuantity = quantities[0] ?? 0;
    const allSame = quantities.every((qty) => qty === firstQuantity);

    return allSame ? firstQuantity : 0;
  }

  /**
   * @returns {HTMLInputElement[]}
   */
  #getSetAllInputs() {
    if (!this.refs.setAllQuantityInput) return [];
    const inputs = Array.isArray(this.refs.setAllQuantityInput)
      ? this.refs.setAllQuantityInput
      : [this.refs.setAllQuantityInput];
    return /** @type {HTMLInputElement[]} */ (inputs);
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  #getSetAllMinusButtons() {
    if (!this.refs.setAllMinusButton) return [];
    const buttons = Array.isArray(this.refs.setAllMinusButton)
      ? this.refs.setAllMinusButton
      : [this.refs.setAllMinusButton];
    return /** @type {HTMLButtonElement[]} */ (buttons);
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  #getSetAllResetButtons() {
    if (!this.refs.setAllResetButton) return [];
    const buttons = Array.isArray(this.refs.setAllResetButton)
      ? this.refs.setAllResetButton
      : [this.refs.setAllResetButton];
    return /** @type {HTMLButtonElement[]} */ (buttons);
  }

  /**
   * Gets the currently visible set-all quantity input
   * @returns {HTMLInputElement | null}
   */
  #getVisibleSetAllInput() {
    const inputs = this.#getSetAllInputs();
    // Find the visible input (check computed display style)
    for (const input of inputs) {
      if (input) {
        const style = window.getComputedStyle(input);
        // Check if the input itself or its ancestors are not hidden
        if (style.display !== 'none' && input.offsetWidth > 0) {
          return input;
        }
      }
    }
    // Fallback to first input
    return inputs[0] || null;
  }

  /**
   * @param {number} value - The new quantity value
   */
  #syncSetAllInputs(value) {
    const inputs = this.#getSetAllInputs();
    inputs.forEach((input) => {
      if (input) {
        input.value = value.toString();
      }
    });
    this.#updateSetAllButtonStates(value);
  }

  /**
   * @param {number} value - The current quantity value
   */
  #updateSetAllButtonStates(value) {
    const minusButtons = this.#getSetAllMinusButtons();
    const resetButtons = this.#getSetAllResetButtons();

    minusButtons.forEach((button) => {
      if (button) {
        button.disabled = value <= 0;
      }
    });

    resetButtons.forEach((button) => {
      if (button) {
        if (value > 0) {
          button.classList.remove('variant-item__remove--hidden');
          button.removeAttribute('tabindex');
          button.removeAttribute('aria-hidden');
        } else {
          button.classList.add('variant-item__remove--hidden');
          button.setAttribute('tabindex', '-1');
          button.setAttribute('aria-hidden', 'true');
        }
      }
    });
  }

  /**
   * Syncs values between inputs and triggers debounced update
   * @param {Event} event - The input event
   */
  #handleSetAllInputChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const value = parseInt(event.target.value, 10) || 0;
    this.#syncSetAllInputs(value);

    this.#debouncedSetAllQuantityUpdate(event);
  };

  /**
   * @param {Event} event - The click event
   */
  increaseSetAllQuantity(event) {
    event.preventDefault();
    const visibleInput = this.#getVisibleSetAllInput();
    if (!visibleInput) return;

    const currentValue = parseInt(visibleInput.value, 10) || 0;
    const step = parseInt(visibleInput.step, 10) || 1;
    const min = parseInt(visibleInput.min, 10) || 0;
    const newValue = currentValue === 0 ? Math.max(min, 1) : currentValue + step;
    this.#syncSetAllInputs(newValue);
    this.#debouncedSetAllQuantityUpdate(event);
  }

  /**
   * @param {Event} event - The click event
   */
  decreaseSetAllQuantity(event) {
    event.preventDefault();
    const visibleInput = this.#getVisibleSetAllInput();
    if (!visibleInput) return;

    const currentValue = parseInt(visibleInput.value, 10) || 0;
    const step = parseInt(visibleInput.step, 10) || 1;
    const min = parseInt(visibleInput.min, 10) || 0;
    const newValue = currentValue <= min ? 0 : Math.max(0, currentValue - step);
    this.#syncSetAllInputs(newValue);
    this.#debouncedSetAllQuantityUpdate(event);
  }

  /**
   * @param {Event} event - The click event
   */
  resetSetAllQuantity(event) {
    event.preventDefault();
    this.#syncSetAllInputs(0);
    this.#debouncedSetAllQuantityUpdate(event);
  }

  /**
   * @returns {Promise<number[]>}
   */
  async #fetchAllProductVariantIds() {
    const productUrl = this.dataset.url;
    if (!productUrl) return [];

    try {
      const handle = productUrl.split('/').pop();
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return [];

      const productData = await response.json();
      return productData.variants.map((/** @type {{ id: number }} */ v) => v.id);
    } catch {
      return [];
    }
  }

  /**
   * @param {Event} _event - The input event
   */
  async #handleSetAllQuantityChange(_event) {
    const visibleInput = this.#getVisibleSetAllInput();
    if (!visibleInput) return;

    const quantity = parseInt(visibleInput.value, 10);

    if (isNaN(quantity) || quantity < 0) {
      return;
    }

    this.#clearSuccessMessage();
    this.#clearErrorMessage();
    this.#clearSetAllError();
    this.#clearAllVariantErrors();

    const setAllMin = parseInt(visibleInput.min, 10) || 0;
    const setAllStep = parseInt(visibleInput.step, 10) || 1;
    let adjustedSetAllQuantity = quantity;
    let setAllWasAdjusted = false;

    if (quantity > 0) {
      const { isValid, adjustedQuantity } = this.#validateStep(quantity, setAllMin === 0 ? 1 : setAllMin, setAllStep);
      if (!isValid) {
        adjustedSetAllQuantity = adjustedQuantity;
        setAllWasAdjusted = true;
        this.#syncSetAllInputs(adjustedSetAllQuantity);
      }
    }

    try {
      const allVariantIds = await this.#fetchAllProductVariantIds();

      if (allVariantIds.length === 0) return;

      this.#applyShimmerEffects(allVariantIds);
      this.#disableQuickOrderListItems();

      this.#abortController?.abort();
      this.#abortController = new AbortController();

      /** @type {Record<string, number>} */
      const updates = {};
      let hasVariantAdjustments = false;

      for (const variantId of allVariantIds) {
        const rules = this.#getVariantQuantityRules(variantId);
        const variantQty = this.#snapQuantityToRules(adjustedSetAllQuantity, rules);
        updates[String(variantId)] = variantQty;

        if (variantQty !== adjustedSetAllQuantity && adjustedSetAllQuantity > 0) {
          hasVariantAdjustments = true;
          const errorMessage = this.#getStepErrorMessage(rules.step, variantQty);
          this.#showVariantError(String(variantId), errorMessage);
        }
      }

      if (setAllWasAdjusted) {
        const errorMessage = this.#getStepErrorMessage(setAllStep, adjustedSetAllQuantity);
        this.#showSetAllError(errorMessage);
      } else if (hasVariantAdjustments) {
        const errorMessage = Theme?.translations?.quantity_step_error_set_all || 'Some variants have different step rules. Values adjusted.';
        this.#showSetAllError(errorMessage);
      }

      if (setAllWasAdjusted || hasVariantAdjustments) {
        setTimeout(() => {
          this.#clearSetAllError();
          this.#clearAllVariantErrors();
        }, 3000);
      }

      const sectionsUrl = new URL(window.location.pathname, window.location.origin);
      sectionsUrl.searchParams.set('page', this.currentPage.toString());

      const body = JSON.stringify({
        updates: updates,
        sections: this.#getSectionIds().join(','),
        sections_url: sectionsUrl.pathname + sectionsUrl.search,
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body }),
        signal: this.#abortController.signal,
      });

      const responseText = await response.text();
      const data = JSON.parse(responseText);

      resetShimmer(this);

      if (data.errors) {
        this.#showErrorMessage(data.errors);
      } else {
        this.#updateSectionHTML(data);

        const successText =
          adjustedSetAllQuantity > 0
            ? Theme?.translations?.quantity_set_success || `Quantity set to ${adjustedSetAllQuantity} for ${allVariantIds.length} variants`
            : Theme?.translations?.all_variants_removed || `All ${allVariantIds.length} variants removed from cart`;

        this.refs.successText.textContent = successText
          .replace('{{ quantity }}', adjustedSetAllQuantity.toString())
          .replace('{{ count }}', allVariantIds.length.toString());
        this.refs.successContainer.classList.remove('hidden');

        document.dispatchEvent(
          new CartAddEvent(data, this.id, {
            source: 'quick-order-set-all',
            sections: data.sections,
          })
        );
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.#enableQuickOrderListItems();
        resetShimmer(this);
        throw error;
      }
    }
  }
}

if (!customElements.get('quick-order-list-component')) {
  customElements.define('quick-order-list-component', QuickOrderListComponent);
}
